import { Dispatch, SetStateAction, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, Input, CardMedia } from "@mui/material";
import { showError, showToast } from "../constants/toasts";
import {
  useAddCategoryMutation,
  useUpdateCategoryMutation,
} from "../service/category";

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedId?: string;
  selectedCategory?: any;
  getCategoryApi: () => void;
}

export default function AddCategory({
  open,
  onClose,
  setOpen,
  selectedId,
  selectedCategory,
  getCategoryApi,
}: AddCategoryModalProps) {
  const [addCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();

  const [error, setError] = useState(false);
  const [image, setImage] = useState<any>();
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState("");

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage("");
      return;
    }

    const MAX_SIZE = 2 * 1024 * 1024; // 2MB

    if (file.size > MAX_SIZE) {
      showError("Image size must be less than 2MB.");
      event.target.value = "";
      return;
    }

    setImage(file);
    setUrl(URL.createObjectURL(file));
  };

  const handleAddCategory = async () => {
    setError(true);
    if (name.trim() === "") {
      showError("Name is required");
      return;
    }

    if (subtitle.trim() === "") {
      showError("Subtitle is required");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("Image", image);
      formData.append("Name", name);
      formData.append("SubTitle", subtitle);
      if (selectedId) {
        formData.append("CategoryId", selectedId);
      }
      let response;
      if (selectedId) {
        response = await updateCategory(formData).unwrap();
      } else {
        response = await addCategory(formData).unwrap();
      }
      if (response?.status === 200) {
        setError(false);
        setOpen(false);
        showToast(
          selectedId
            ? "Category updated successfully"
            : "Category added successfully"
        );
        getCategoryApi();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedCategory) {
      setName(selectedCategory?.name || "");
      setImage(selectedCategory?.imageUrl);
      setSubtitle(selectedCategory?.subTitle || "");
      setUrl(selectedCategory?.imageUrl);
    } else if (open && !selectedCategory) {
      setName("");
      setImage("");
      setSubtitle("");
      setUrl("");
    }
  }, [open, selectedCategory]);

  return (
    <Modal className="modal addCategory_modal" open={open} onClose={onClose}>
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title text_center mb_30">
            <h2>{selectedId ? "Edit Category" : "Add Category"}</h2>
            <div
              className="btn-close"
              tabIndex={0}
              role="button"
              onClick={() => {
                setError(false);
                setOpen(false);
                setName("");
                setImage("");
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault(); // prevent default page reload
              handleAddCategory();
            }}
          >
            <div className="control_group w_100">
              <label htmlFor="icon-button-file">Upload Image</label>
              {url ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={url} alt="photo" />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        setImage("");
                        setUrl("");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(e: any) => {
                      handleImageUpload(e);
                      e.target.value = null;
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group">
              <label htmlFor="categoryName">Category Name</label>
              <TextField
                id="categoryName"
                aria-labelledby="categoryNameLabel"
                placeholder="Enter Category Name"
                fullWidth
                hiddenLabel
                value={name}

                onChange={(e) => {
                  if (e.target.value === " " || e.target.value === ".") {
                  } else {
                    setName(e.target.value);
                  }
                }}
                inputProps={{
                  maxLength: 40,
                  'aria-label': 'Category Name',
                }}
              />
              {error && name == "" ? (
                <h6 className="err_msg">Name is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="control_group">
              <label htmlFor="categorySubtitle">Category Subtitle</label>
              <TextField
                id="categorySubtitle"
                aria-labelledby="categorySubtitleLabel"
                placeholder="Enter Category Subtitle"
                fullWidth
                hiddenLabel
                value={subtitle}
                inputProps={{ maxLength: 40 }}
                onChange={(e) => {
                  if (e.target.value === " " || e.target.value === ".") {
                  } else {
                    setSubtitle(e.target.value);
                  }
                }}
              />
              {error && subtitle == "" ? (
                <h6 className="err_msg">Subtitle is required</h6>
              ) : (
                ""
              )}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={loading}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
